import { gql, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import { AvailableUsersContext } from "./AvailableUsersContext.js";
import {
  AvailableUsersQuery,
  useAvailableUsersQuery,
} from "./AvailableUsersProvider.generated.js";
import { useAuth } from "./useAuth.js";

export const AvailableUsersProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const { isAuthenticated } = useAuth();

  const { availableUsers, isLoading, hasResult, refetch } = useAvailableUsers({
    skip: !isAuthenticated,
  });

  return (
    <AvailableUsersContext.Provider
      value={{ availableUsers, isLoading, hasResult, refetch }}
    >
      {children}
    </AvailableUsersContext.Provider>
  );
};

const EMPTY_ARRAY: Extract<
  AvailableUsersQuery["availableUsers"],
  { __typename: "AvailableUsersResult" }
>["availableUsers"] = [];
function useAvailableUsers({ skip }: { skip: boolean }) {
  const client = useApolloClient();
  const query = useAvailableUsersQuery({ client, skip });

  const availableUsers =
    getDataOrNull(query.data?.availableUsers)?.availableUsers ?? EMPTY_ARRAY;

  return {
    availableUsers,
    isLoading: query.loading,
    // With cache-and-network `data` holds an earlier cached list while the
    // request is still loading, and after it failed; only a list this request
    // confirmed counts.
    hasResult:
      !query.loading &&
      !query.error &&
      query.data?.availableUsers.__typename === "AvailableUsersResult",
    refetch: query.refetch,
  };
}
